import { match } from "ts-pattern";
import z from "zod";

import { SUPPLEMENT_KEYWORD } from "./authoring.js";
import type { JsonValue } from "./document.js";
import {
  commentTargetOf,
  type PublishedGroupSchema,
  type PublishedInstrumentSchema,
} from "./publishedSchema.js";
import type { AssessmentResponseUnbranded } from "./response.js";

/**
 * The response an item holds where it was sent without a rating: a fact
 * rather than a score, as every string response is, so no aggregate sums it
 * and no reading takes it for a rating.
 */
export const NOT_ANSWERED = "not_answered";

/** Whether a response says its item was sent without a rating. */
export const isNotAnswered = (response: JsonValue | undefined): boolean =>
  response === NOT_ANSWERED;

/**
 * What a submission does with an item nobody answered: `refused`, so every
 * item the instrument requires carries an answer, or `sent_not_answered`,
 * where an item may hold {@link NOT_ANSWERED} in place of its answer. Which
 * applies is decided by the route a submission arrives by, never by the
 * request.
 */
export const UnansweredItemTreatmentSchema = z.enum([
  "refused",
  "sent_not_answered",
]);

export type UnansweredItemTreatment = z.infer<
  typeof UnansweredItemTreatmentSchema
>;

/** A group whose every item but a comment also accepts {@link NOT_ANSWERED}. */
const groupAdmittingNotAnswered = (
  group: PublishedGroupSchema,
): PublishedGroupSchema => ({
  ...group,
  properties: Object.fromEntries(
    Object.entries(group.properties).map(([key, item]) => [
      key,
      item[SUPPLEMENT_KEYWORD] === undefined
        ? {
            ...item,
            properties: {
              response: {
                anyOf: [item.properties.response, { const: NOT_ANSWERED }],
              },
            },
          }
        : item,
    ]),
  ),
});

/**
 * The schema a submission's document is validated against: the pinned one
 * as stored, or, where unanswered items are sent as not answered, the same
 * with every item but a comment also accepting {@link NOT_ANSWERED}. What the
 * instrument requires stays required either way, so an item is answered or
 * says it was not, and is never simply absent.
 */
export const submissionSchemaFor = ({
  schema,
  unansweredItemTreatment,
}: {
  schema: PublishedInstrumentSchema;
  unansweredItemTreatment: UnansweredItemTreatment;
}): PublishedInstrumentSchema =>
  match(unansweredItemTreatment)
    .with("refused", () => schema)
    .with("sent_not_answered", () => ({
      ...schema,
      properties: Object.fromEntries(
        Object.entries(schema.properties).map(([key, group]) => [
          key,
          groupAdmittingNotAnswered(group),
        ]),
      ),
    }))
    .exhaustive();

/** How many of the items a submission sent were answered, of every item it sent. */
export type ItemsAnswered = { answered: number; of: number };

/**
 * How many of the items a submission holds were answered rather than sent
 * not answered. A comment is not an item, so is not counted.
 */
export const itemsAnsweredIn = ({
  schema,
  responses,
}: {
  schema: PublishedInstrumentSchema;
  responses: readonly AssessmentResponseUnbranded[];
}): ItemsAnswered => {
  const items = responses.filter(({ group, item }) => {
    const itemSchema = schema.properties[group]?.properties[item];

    return (
      itemSchema === undefined || commentTargetOf(itemSchema) === undefined
    );
  });

  return {
    answered: items.filter(({ response }) => !isNotAnswered(response)).length,
    of: items.length,
  };
};
